import React, { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const ALL_DEPARTMENTS = "ALL";

const getErrorMessage = error => error?.response?.data?.message
  || (typeof error?.response?.data === "string" ? error.response.data : null)
  || "Operacja nie powiodła się.";

const pad = value => String(value).padStart(2, "0");
const toMonthValue = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
const parseMonthValue = value => {
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1);
};
const isoDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const weekday = date => new Intl.DateTimeFormat("pl-PL", { weekday: "short" }).format(date).replace(".", "");

const mergeDepartmentMonths = months => {
  const dayMap = new Map();
  const employees = [];

  months.forEach(month => {
    (month?.days || []).forEach(day => {
      const current = dayMap.get(day.date) || {
        date: day.date,
        approvedCount: 0,
        pendingCount: 0,
        limitReached: false,
        limitExceeded: false,
        limitDetails: []
      };
      current.approvedCount += Number(day.approvedCount || 0);
      current.pendingCount += Number(day.pendingCount || 0);
      current.limitReached = current.limitReached || !!day.limitReached;
      current.limitExceeded = current.limitExceeded || !!day.limitExceeded;
      if ((day.limitReached || day.limitExceeded) && day.limit != null) {
        current.limitDetails.push(`${month.departmentName}: ${day.approvedCount || 0}/${day.limit}`);
      }
      dayMap.set(day.date, current);
    });

    (month?.employees || []).forEach(employee => {
      employees.push({
        ...employee,
        departmentId: month.departmentId,
        departmentName: month.departmentName
      });
    });
  });

  employees.sort((a, b) => {
    const departmentCompare = (a.departmentName || "").localeCompare(b.departmentName || "", "pl");
    if (departmentCompare !== 0) return departmentCompare;
    return (a.employeeFullName || "").localeCompare(b.employeeFullName || "", "pl");
  });

  return {
    departmentId: null,
    departmentName: "Wszystkie",
    days: Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
    employees
  };
};

const DepartmentVacationCalendar = () => {
  const axiosPrivate = useAxiosPrivate();
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(ALL_DEPARTMENTS);
  const [monthDate, setMonthDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDepartments = async () => {
    try {
      const response = await axiosPrivate.get("/department-vacations/departments");
      const items = response.data || [];
      setDepartments(items);
      setDepartmentId(current => current === ALL_DEPARTMENTS || items.some(item => String(item.id) === String(current))
        ? current
        : ALL_DEPARTMENTS);
    } catch (error) {
      setDepartments([]);
      toastError(getErrorMessage(error));
    }
  };

  const loadDepartmentMonth = async id => {
    const response = await axiosPrivate.get("/department-vacations/month", {
      params: {
        departmentId: id,
        year: monthDate.getFullYear(),
        month: monthDate.getMonth() + 1
      }
    });
    return response.data;
  };

  const loadMonth = async () => {
    setLoading(true);
    try {
      if (departmentId === ALL_DEPARTMENTS) {
        const months = await Promise.all(departments.map(department => loadDepartmentMonth(department.id)));
        setData(mergeDepartmentMonths(months));
      } else if (departmentId) {
        setData(await loadDepartmentMonth(departmentId));
      } else {
        setData(null);
      }
    } catch (error) {
      setData(null);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, [axiosPrivate]);

  useEffect(() => {
    loadMonth();
  }, [axiosPrivate, departmentId, monthDate, departments]);

  const days = useMemo(() => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const count = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: count }, (_, index) => new Date(year, month, index + 1));
  }, [monthDate]);

  const daySummary = useMemo(() => {
    const map = new Map();
    (data?.days || []).forEach(day => map.set(day.date, day));
    return map;
  }, [data]);

  const periodForDate = (periods, date) => {
    const key = isoDate(date);
    return (periods || []).filter(period => period.dateFrom <= key && period.dateTo >= key);
  };

  const changeMonth = delta => {
    setMonthDate(current => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  return (
    <div id="layoutSidenav_content" className="department-vacation-page">
      <ToastContainer />
      <div className="app-component-card-header shadow department-vacation-header">
        <div>
          <div className="my-schedule-title">Urlopy działów</div>
          <div className="my-schedule-subtitle">Miesięczny widok zatwierdzonych urlopów, oczekujących wniosków i dziennego wykorzystania limitów ustawionych w działach.</div>
        </div>
      </div>

      <div className="container-fluid px-4 department-vacation-content">
        <div className="schedule-section department-vacation-toolbar">
          <Form.Group>
            <Form.Label>Dział</Form.Label>
            <Form.Select value={departmentId} onChange={event => setDepartmentId(event.target.value)}>
              <option value={ALL_DEPARTMENTS}>Wszystkie</option>
              {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Miesiąc</Form.Label>
            <div className="department-vacation-month-control">
              <Button variant="outline-secondary" onClick={() => changeMonth(-1)}>‹</Button>
              <Form.Control type="month" value={toMonthValue(monthDate)} onChange={event => setMonthDate(parseMonthValue(event.target.value))}/>
              <Button variant="outline-secondary" onClick={() => changeMonth(1)}>›</Button>
            </div>
          </Form.Group>
        </div>

        <div className="department-vacation-legend">
          <span><i className="department-vacation-dot approved"/> Zatwierdzony urlop</span>
          <span><i className="department-vacation-dot pending"/> Wniosek oczekujący</span>
          <span><i className="department-vacation-dot limit"/> Osiągnięty/przekroczony limit działu</span>
        </div>

        {loading ? <LoadingDataDiv /> : !data ? (
          <div className="my-schedule-empty">Brak danych do wyświetlenia.</div>
        ) : (
          <div className="department-vacation-table-wrap">
            <table className="department-vacation-table">
              <thead>
                <tr>
                  <th className="department-vacation-employee-col">Pracownik</th>
                  {days.map(date => {
                    const summary = daySummary.get(isoDate(date));
                    const weekend = [0, 6].includes(date.getDay());
                    return (
                      <th key={isoDate(date)} className={`${weekend ? "weekend" : ""} ${summary?.limitExceeded ? "limit-exceeded" : summary?.limitReached ? "limit-reached" : ""}`}>
                        <span>{date.getDate()}</span>
                        <small>{weekday(date)}</small>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(data.employees || []).map(employee => (
                  <tr key={`${employee.departmentId || data.departmentId || "department"}-${employee.employeeId}`}>
                    <th className="department-vacation-employee-col">
                      <span>{employee.employeeFullName}</span>
                      {departmentId === ALL_DEPARTMENTS && <small>{employee.departmentName}</small>}
                    </th>
                    {days.map(date => {
                      const periods = periodForDate(employee.periods, date);
                      const approved = periods.find(period => period.status === "APPROVED");
                      const pending = periods.find(period => period.status === "PENDING");
                      const period = approved || pending;
                      const title = periods.map(item => `${item.status === "PENDING" ? "Oczekuje" : "Zatwierdzony"}: ${item.absenceTypeName || "urlop"} ${item.dateFrom}–${item.dateTo}`).join("\n");
                      return (
                        <td
                          key={isoDate(date)}
                          className={`${period ? `vacation-${period.status.toLowerCase()}` : ""} ${[0, 6].includes(date.getDay()) ? "weekend" : ""}`}
                          title={title}
                          style={approved?.color ? { "--vacation-type-color": approved.color } : undefined}
                        >
                          {period && <span>{period.absenceTypeShortName || (period.status === "PENDING" ? "?" : "U")}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="department-vacation-employee-col">Na urlopie</th>
                  {days.map(date => {
                    const summary = daySummary.get(isoDate(date));
                    const titleParts = [];
                    if (summary?.pendingCount) titleParts.push(`Oczekujące wnioski: ${summary.pendingCount}`);
                    if (summary?.limitDetails?.length) titleParts.push(`Limity: ${summary.limitDetails.join(", ")}`);
                    return (
                      <td key={isoDate(date)} className={`${summary?.limitExceeded ? "limit-exceeded" : summary?.limitReached ? "limit-reached" : ""}`} title={titleParts.join("\n")}>
                        <strong>{summary?.approvedCount || 0}</strong>
                        {!!summary?.pendingCount && <small>+{summary.pendingCount}</small>}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentVacationCalendar;
